import { InputAdornment, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { differenceInCalendarYears, isSameYear } from 'date-fns';
import React from 'react';
import { TFunction } from 'react-i18next';
import { usePrefixTranslation } from '../../../../i18n';
import { actions, useTypedDispatch, useTypedSelector } from '../../../../redux';
import { theme } from '../../../../theme/theme';
import { Asset } from '../../../../types/asset';
import { InspectionActivity, Plan } from '../../../../types/plan';
import { Checkbox, CheckCard, DatePicker, Property, TextField } from '../../../global';
import { SectionState } from '../InspectionCreationWizard';

const mod = 'sections.InspectionTypes';

/**
 * Number of internal steps this section contains.
 */
export const steps = 1;
export const label = `${mod}.summary-label`;

/**
 * Header generator.
 *
 * @param t translation function
 * @param state the current section state
 * @param focused if is the current section
 * @param asset selected asset
 * @param plan asset plan
 * @returns header title and subtitle
 */
export const header = (t: TFunction<string[]>, state: SectionState, focused: boolean, asset?: Asset, plan?: Plan) => ({
    title: t(`${mod}.header-title${focused ? '' : '-small'}`),
    subtitle: t(`${mod}.header-subtitle${focused ? '' : '-small'}`, { count: 1 }),
});

const useInspectionTypesClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        margin: '-10px 0',
        '& > div': {
            margin: '10px 0',
        },
    },
    inputs: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        '& > *': {
            flex: '0 0 300px',
            marginBottom: '0 !important',
        },
    },
    activityCard: {
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 5,
        padding: 20,
        '& > span': {
            color: theme.colors.neutral[1],
            ...theme.typography.headings.h5,
            marginBottom: 20,
        },
        '&.internal': {
            background: theme.colors.green[5],
        },
        '&.external': {
            background: theme.colors.amber[4],
        },
        '&.readonly': {
            padding: 10,
        },
    },
    activities: {
        display: 'flex',
        flexWrap: 'wrap',
        margin: -10,
        '&.readonly': {
            margin: -5,
        },
        '& > div': {
            flex: '1 0 calc(100% / 3 - 50px)',
            minWidth: 200,
            margin: 10,
        },
        '&.readonly > div': {
            flex: '1 0 calc(100% / 2 - 50px)',
            minWidth: 200,
            margin: 5,
        },
    },
    aside: {
        display: 'flex',
        alignItems: 'center',
        color: theme.colors.darkBlue[5],
        ...theme.typography.content.label.medium,
    },
});

/**
 * Type and method section of inspection creation process.
 * TODO Currently this section is using a hack (set \n characters) to toggle enable and disabled, so the activities
 * information is not lost.
 *
 * @param props.state state of the plan in the inspection plan creation flow
 * @param props.next received function to proceed to next step
 */
export const Component = (props: { state: SectionState; next: () => void }) => {
    const classes = useInspectionTypesClasses();
    const { t } = usePrefixTranslation('inspection-creation', { 'inspection-creation': 'sections.InspectionTypes' });
    const dispatch = useTypedDispatch();
    const { plan, version, activities } = useTypedSelector(state => ({
        plan: state.plan.changed,
        version: state.plan.changes.length,
        activities: state.plan_activity.items,
    }));
    const internalActivity = plan?.inspection_activity?.find(c => c.type === 'internal');
    const externalActivity = plan?.inspection_activity?.find(c => c.type === 'external');
    const status = {
        editable: props.state.editable,
        readonly: !props.state.editable,
    };

    React.useEffect(() => {
        dispatch(actions.plan_activity.get());
    }, []);

    const toggleActivity = (type: InspectionActivity['type'], action: 'add' | 'delete') => {
        if (!plan) return;
        const base: InspectionActivity = {
            id: -1,
            inspection_plan: -1,
            type,
            required: false,
            comments: '',
            asset_state: '',
            preparation_steps: [],
            methods: {},
            last_performed: new Date(0),
        };
        const activity = type === 'internal' ? { ...(internalActivity ?? base) } : { ...(externalActivity ?? base) };
        activity.required = action === 'add';
        const activities = [activity, (type === 'internal' ? externalActivity : internalActivity) ?? base];
        dispatch(actions.plan.edit({ inspection_activity: activities }));
    };

    const toggleMethod = (activity: InspectionActivity, method: string, action: 'add' | 'delete') => {
        if (!plan) return;
        const base: InspectionActivity = {
            id: -1,
            inspection_plan: -1,
            type: activity.type === 'internal' ? 'external' : 'internal',
            required: false,
            comments: '',
            asset_state: '',
            preparation_steps: [],
            methods: {},
            last_performed: new Date(0),
        };
        const activityCopy: InspectionActivity = { ...activity };
        const methods: InspectionActivity['methods'] = JSON.parse(JSON.stringify(activityCopy.methods));
        activityCopy.methods = methods;
        const deleteKey = action === 'add' ? `\n${method}` : method;
        const addKey = action === 'add' ? method : `\n${method}`;
        const tasks = activityCopy.methods[deleteKey] ?? [];
        delete activityCopy.methods[deleteKey];
        activityCopy.methods[addKey] = tasks;
        const activities = [activityCopy, (activity.type === 'internal' ? externalActivity : internalActivity) ?? base];
        dispatch(actions.plan.edit({ inspection_activity: activities }));
    };

    const updateComment = (type: 'internal' | 'external', comments: string) => {
        if (!plan) return;
        const base: InspectionActivity = {
            id: -1,
            inspection_plan: -1,
            type,
            required: false,
            comments: '',
            asset_state: '',
            preparation_steps: [],
            methods: {},
            last_performed: new Date(0),
        };
        const activity = type === 'internal' ? { ...(internalActivity ?? base) } : { ...(externalActivity ?? base) };
        activity.comments = comments;
        const activities = [activity, (type === 'internal' ? externalActivity : internalActivity) ?? base];
        dispatch(actions.plan.edit({ inspection_activity: activities }));
    };

    if (!plan) return <span>{'#plan not in store'}</span>;

    return (
        <div className={classes.root}>
            {([
                [internalActivity, 'internal'],
                [externalActivity, 'external'],
            ] as const).map(([activity, type]) => (
                <React.Fragment key={type}>
                    {props.state.editable && (
                        <CheckCard
                            title={t('inspection', { context: type })}
                            subtitle={t('performed', {
                                context:
                                    !activity?.last_performed || +activity.last_performed === 0
                                        ? 'never'
                                        : isSameYear(new Date(), activity!.last_performed!)
                                        ? 'zero'
                                        : 'interval',
                                count: differenceInCalendarYears(new Date(), activity!?.last_performed!),
                            })}
                            editable={props.state.editable}
                            checked={!!activity?.required}
                            onCheck={value => toggleActivity(type, value ? 'add' : 'delete')}
                            aside={
                                <span className={classes.aside}>
                                    <Checkbox
                                        checked={!activity?.required}
                                        onChange={e => toggleActivity(type, e.target.checked ? 'delete' : 'add')}
                                    />
                                    {t('not-required')}
                                </span>
                            }
                        />
                    )}
                    {props.state.editable && !!activity?.required && (
                        <div className={classes.inputs}>
                            <DatePicker
                                label={t('input-due-date')}
                                value={plan[`${type}_due_date` as const]}
                                onChange={date =>
                                    date && dispatch(actions.plan.edit({ [`${type}_due_date` as const]: date }))
                                }
                            />
                            <TextField
                                key={version}
                                label={t('input-interval')}
                                type='number'
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position='end'>{t('input-interval-months')}</InputAdornment>
                                    ),
                                }}
                                defaultValue={plan[`${type}_inspection_interval` as const]}
                                onBlur={e =>
                                    dispatch(
                                        actions.plan.edit({
                                            [`${type}_inspection_interval` as const]: Math.max(+e.target.value, 0),
                                        }),
                                    )
                                }
                            />
                        </div>
                    )}
                    {!!activity?.required && (
                        <div className={clsx(classes.activityCard, activity.type, status)}>
                            {props.state.editable && <span>{t(`inspection-activities-${type}`)}</span>}
                            <div className={clsx(classes.activities, status)}>
                                {activities
                                    .sort((a, b) => a.localeCompare(b))
                                    .map(method => {
                                        const checked = method in activity.methods;
                                        if (!props.state.editable && !checked) return undefined;
                                        return (
                                            <CheckCard
                                                key={method}
                                                badge={
                                                    !props.state.editable
                                                        ? {
                                                              type: 'neutral',
                                                              children: activity.type === 'external' ? 'ext' : 'int',
                                                          }
                                                        : undefined
                                                }
                                                title={method}
                                                subtitle={t('performed', {
                                                    context:
                                                        !activity?.last_performed || +activity?.last_performed === 0
                                                            ? 'never'
                                                            : isSameYear(new Date(), activity!.last_performed!)
                                                            ? 'zero'
                                                            : 'interval',
                                                    count: differenceInCalendarYears(
                                                        new Date(),
                                                        activity!.last_performed!,
                                                    ),
                                                })}
                                                editable={props.state.editable}
                                                checked={checked}
                                                onCheck={checked =>
                                                    toggleMethod(activity, method, checked ? 'add' : 'delete')
                                                }
                                            />
                                        );
                                    })}
                            </div>
                        </div>
                    )}
                    {!activity?.required && (
                        <Property
                            name={t('comments')}
                            value={activity?.comments ?? ''}
                            editable={props.state.editable}
                            multiline
                            rows={5}
                            onChange={(name, value) => updateComment(type, value ?? '')}
                        />
                    )}
                </React.Fragment>
            ))}
        </div>
    );
};
